import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const PasswordResetSuccessPage = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/user-login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleContinueToSignIn = () => {
    navigate('/user-login');
  };

  return (
    <>
      <Helmet>
        <title>Password Reset Successful - Smart Cities Onboarding Platform</title>
        <meta name="description" content="Your password has been successfully reset" />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-card border border-border rounded-lg shadow-soft p-8 max-w-md w-full">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mx-auto mb-6">
              <Icon name="CheckCircle" size={48} className="text-success" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-3">
              Password Reset Successful!
            </h1>
            <p className="text-muted-foreground">
              Your password has been successfully updated. Your account is now secure with your new password.
            </p>
          </div>

          {/* Security Recommendations */}
          <div className="bg-info/5 border border-info/20 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="Shield" size={20} className="text-info mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-foreground mb-2">Security Recommendations</h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Update saved passwords in your browser and password managers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Enable two-factor authentication for enhanced security</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Avoid reusing this password on other platforms</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground">
              Redirecting to sign in page in{' '}
              <span className="font-semibold text-primary">{countdown}</span> seconds...
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              variant="default"
              size="lg"
              fullWidth
              onClick={handleContinueToSignIn}
              iconName="LogIn"
              iconPosition="left"
            >
              Continue to Sign In
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate('/')}
              iconName="Home"
              iconPosition="left"
            >
              Go to Home
            </Button>
          </div>

          {/* Additional Information */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-center space-y-2">
              <p className="text-xs text-muted-foreground">
                Need help with your account?
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs">
                <Button variant="link" className="text-xs p-0 h-auto">
                  Contact Support
                </Button>
                <span className="text-muted-foreground">•</span>
                <Button variant="link" className="text-xs p-0 h-auto">
                  Security Resources
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PasswordResetSuccessPage;